/**
 * Low level, dependency free scanning helpers shared by the Deal Model DSL
 * parser, printer, completion source and CodeMirror tokenizer.
 *
 * Every helper is string aware: ZEN expressions may embed single quoted,
 * double quoted and backtick quoted literals, and a `#` (note marker) or a
 * `,` (tier separator) inside such a literal must never be treated as
 * structure.
 */

export const NOTE_MARKER = '#'

const OPENING_BRACKETS = new Set(['(', '[', '{'])
const CLOSING_BRACKETS = new Set([')', ']', '}'])

export interface ScanState {
  /** Quote character currently open, or null when outside a string literal. */
  quote: string | null
  /** Bracket nesting depth outside string literals. */
  depth: number
}

export function initialScanState(): ScanState {
  return { quote: null, depth: 0 }
}

/**
 * Advance `state` across one character. Returns true when the character sits
 * at the structural top level (outside any string literal and at bracket
 * depth zero) *before* the character was consumed.
 */
export function scanChar(state: ScanState, char: string, previousWasEscape: boolean): { topLevel: boolean; escape: boolean } {
  if (state.quote !== null) {
    if (previousWasEscape) return { topLevel: false, escape: false }
    if (char === '\\') return { topLevel: false, escape: true }
    if (char === state.quote) state.quote = null
    return { topLevel: false, escape: false }
  }
  if (char === '"' || char === "'" || char === '`') {
    state.quote = char
    return { topLevel: false, escape: false }
  }
  const topLevel = state.depth === 0
  if (OPENING_BRACKETS.has(char)) state.depth += 1
  else if (CLOSING_BRACKETS.has(char)) state.depth = Math.max(0, state.depth - 1)
  return { topLevel, escape: false }
}

/**
 * Offsets of every occurrence of `char` outside string literals. When
 * `requireTopLevel` is true the occurrence must also sit at bracket depth 0.
 */
export function unquotedOffsets(text: string, char: string, requireTopLevel: boolean): number[] {
  const state = initialScanState()
  const offsets: number[] = []
  let escape = false
  for (let index = 0; index < text.length; index += 1) {
    const current = text[index] as string
    const wasQuoted = state.quote !== null
    const depthBefore = state.depth
    const step = scanChar(state, current, escape)
    escape = step.escape
    if (wasQuoted || state.quote !== null) continue
    if (current !== char) continue
    if (requireTopLevel && depthBefore !== 0) continue
    offsets.push(index)
  }
  return offsets
}

/** Offsets of every occurrence of `char` that sits outside strings at depth 0. */
export function topLevelOffsets(text: string, char: string): number[] {
  return unquotedOffsets(text, char, true)
}

/**
 * Index of the first occurrence of `needle` that starts outside any string
 * literal, or -1. Used to find structural words such as ` if ` inside an
 * outcome tier list without tripping over quoted text.
 */
export function indexOfUnquoted(text: string, needle: string): number {
  const state = initialScanState()
  let escape = false
  for (let index = 0; index < text.length; index += 1) {
    const current = text[index] as string
    const wasQuoted = state.quote !== null
    const step = scanChar(state, current, escape)
    escape = step.escape
    if (wasQuoted || state.quote !== null) continue
    if (text.startsWith(needle, index)) return index
  }
  return -1
}

/** Split on top level occurrences of a single separator character. */
export function splitTopLevel(text: string, separator: string): string[] {
  const offsets = topLevelOffsets(text, separator)
  if (offsets.length === 0) return [text]
  const parts: string[] = []
  let start = 0
  for (const offset of offsets) {
    parts.push(text.slice(start, offset))
    start = offset + 1
  }
  parts.push(text.slice(start))
  return parts
}

export interface NoteSplit {
  /** The line with any trailing note removed (trailing whitespace trimmed). */
  code: string
  /** The note text, without its marker and leading whitespace. */
  note: string | null
  /** Absolute offset of the note marker inside the original line, or -1. */
  markerOffset: number
}

/**
 * Split a source line into its code part and an optional trailing `# note`.
 * A `#` inside a string literal is ignored. `##` at the very start of a line
 * is a group header and is handled by the parser before this is called.
 */
export function splitTrailingNote(line: string): NoteSplit {
  const offsets = unquotedOffsets(line, NOTE_MARKER, false)
  const marker = offsets.length > 0 ? (offsets[0] as number) : -1
  if (marker < 0) return { code: line.replace(/\s+$/u, ''), note: null, markerOffset: -1 }
  const note = line.slice(marker + 1).replace(/^\s+/u, '').replace(/\s+$/u, '')
  return { code: line.slice(0, marker).replace(/\s+$/u, ''), note: note.length > 0 ? note : null, markerOffset: marker }
}

const SIMPLE_ESCAPES: Record<string, string> = { n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', '"': '"', "'": "'", '`': '`', '\\': '\\', '/': '/' }

/**
 * Decode a quoted token (`"…"`, `'…'` or `` `…` ``). Returns null when the
 * token is not a complete quoted literal.
 */
export function decodeQuoted(token: string): string | null {
  if (token.length < 2) return null
  const quote = token[0] as string
  if (quote !== '"' && quote !== "'" && quote !== '`') return null
  if (token[token.length - 1] !== quote) return null
  let out = ''
  for (let index = 1; index < token.length - 1; index += 1) {
    const char = token[index] as string
    if (char === quote) return null
    if (char !== '\\') { out += char; continue }
    const next = token[index + 1]
    if (next === undefined) return null
    if (next === 'u') {
      const hex = token.slice(index + 2, index + 6)
      if (!/^[0-9a-fA-F]{4}$/u.test(hex)) return null
      out += String.fromCharCode(Number.parseInt(hex, 16))
      index += 5
      continue
    }
    const simple = SIMPLE_ESCAPES[next]
    if (simple === undefined) return null
    out += simple
    index += 1
  }
  return out
}

/** Encode a string as a double quoted DSL literal. */
export function encodeQuoted(value: string): string {
  return JSON.stringify(value)
}

/** Collapse a free text note or label onto a single line. */
export function singleLine(value: string): string {
  return value.replace(/[\r\n\t]+/gu, ' ').replace(/\s{2,}/gu, ' ').replace(/^\s+|\s+$/gu, '')
}
